import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Palette, RotateCcw, Save, Check, Wand2 } from "lucide-react";
import { ThemePreviewCard } from "@/components/ThemePreviewCard";
import { paletteFromLogo } from "@/lib/logoPalette";
import { api, errMsg } from "@/lib/api";
import { PALETTE_KEYS, PALETTE_PRESETS, DEFAULT_PALETTE } from "@/lib/brand";

const isHex = (v) => /^#[0-9a-fA-F]{6}$/.test(v || "");
const same = (a, b) => PALETTE_KEYS.every(([k]) => (a[k] || "").toLowerCase() === (b[k] || "").toLowerCase());
const preview = (p) => window.dispatchEvent(new CustomEvent("brand-preview", { detail: p }));

const ColorField = ({ k, label, hint, value, onChange }) => {
  const [text, setText] = useState(value);
  useEffect(() => { setText(value); }, [value]);
  const commit = (v) => { setText(v); if (isHex(v)) onChange(v.toLowerCase()); };
  return (
    <div className="tc-field" data-testid={`theme-field-${k}`}>
      <span className="pick" style={{ background: value }}><input type="color" value={value} onChange={(e) => commit(e.target.value)} aria-label={`${label} colour`} data-testid={`theme-color-${k}`} /></span>
      <div className="tx"><b>{label}</b><small>{hint}</small></div>
      <input className={`hex ${text && !isHex(text) ? "bad" : ""}`} value={text} maxLength={7} spellCheck={false} onChange={(e) => commit(e.target.value.startsWith("#") ? e.target.value : `#${e.target.value}`)} data-testid={`theme-hex-${k}`} />
    </div>
  );
};

export const ThemeCustomiser = () => {
  const [saved, setSaved] = useState(DEFAULT_PALETTE);
  const [p, setP] = useState(DEFAULT_PALETTE);
  const [busy, setBusy] = useState(false);
  const [brand, setBrand] = useState(null);
  const dirtyRef = useRef(false);
  useEffect(() => {
    api.get("/settings/brand").then((r) => { setBrand(r.data); const cur = { ...DEFAULT_PALETTE, ...(r.data.palette || {}) }; setSaved(cur); setP(cur); }).catch(() => {});
    return () => { if (dirtyRef.current) window.dispatchEvent(new Event("brand-changed")); };
  }, []);
  const update = (next) => { setP(next); dirtyRef.current = true; preview(next); };
  const save = async () => {
    setBusy(true);
    try { const r = await api.put("/settings/brand", { palette: p }); const cur = { ...DEFAULT_PALETTE, ...(r.data.palette || {}) }; setSaved(cur); setP(cur); dirtyRef.current = false; window.dispatchEvent(new Event("brand-changed")); toast.success("Theme saved — it now applies everywhere for your account"); } catch (ex) { toast.error(errMsg(ex)); }
    setBusy(false);
  };
  const reset = async () => {
    setBusy(true);
    try { await api.put("/settings/brand", { palette: null }); setSaved(DEFAULT_PALETTE); setP(DEFAULT_PALETTE); dirtyRef.current = false; window.dispatchEvent(new Event("brand-changed")); toast.success("Back to Electric Lime on black"); } catch (ex) { toast.error(errMsg(ex)); }
    setBusy(false);
  };
  const matchLogo = async () => {
    setBusy(true);
    try { const pal = await paletteFromLogo(brand?.logoKey); update(pal); toast.success("Palette built from your logo — tweak anything, then save"); } catch (ex) { toast.error(ex.message); }
    setBusy(false);
  };
  const dirty = !same(p, saved);
  return (
    <div className="panel" data-testid="theme-customiser">
      <div className="ph"><h3><Palette size={15} style={{ verticalAlign: -2, marginRight: 6 }} />Theme · colour palette</h3><span className="tc-live"><i /> Live preview — changes show instantly, save to keep them</span></div>
      <label style={{ marginTop: 6 }}>Presets · one click applies the whole palette</label>
      <div className="tc-presets" data-testid="theme-presets">
        {PALETTE_PRESETS.map(([name, pal], i) => (
          <button type="button" key={name} className={`tc-preset ${same(p, pal) ? "on" : ""}`} onClick={() => update({ ...pal })} data-testid={`theme-preset-${i}`}>
            <span className="sw" style={{ background: pal.bg }}><i style={{ background: pal.nav }} /><i style={{ background: pal.surface }} /><i style={{ background: pal.accent }} /><i style={{ background: pal.ink }} /></span>
            <b>{name}{same(p, pal) && <Check size={12} style={{ marginLeft: 6, verticalAlign: -1 }} />}</b>
          </button>
        ))}
      </div>
      <div className="tc-split">
        <div>
          <label>Fine-tune · full-spectrum picker or exact hex</label>
          <div className="tc-grid">
            {PALETTE_KEYS.map(([k, label, hint]) => <ColorField key={k} k={k} label={label} hint={hint} value={p[k]} onChange={(v) => update({ ...p, [k]: v })} />)}
          </div>
          <button type="button" className="btn g" style={{ marginTop: 12, gap: 8 }} disabled={busy} onClick={matchLogo} title={brand?.logoKey ? "Build a palette from your logo's colours" : "Upload a logo under Branding first"} data-testid="theme-match-logo"><Wand2 size={14} /> Match my logo</button>
        </div>
        <div>
          <label>Client view · updates as you tweak</label>
          <ThemePreviewCard brand={brand} palette={p} />
        </div>
      </div>
      <div className="tc-actions">
        <button type="button" className="btn" disabled={busy || !dirty} onClick={save} data-testid="theme-save"><Save size={14} /> Save theme</button>
        <button type="button" className="btn g" disabled={busy} onClick={reset} data-testid="theme-reset"><RotateCcw size={14} /> Reset to default</button>
        {dirty && <span className="ss" data-testid="theme-unsaved">Unsaved changes — leaving this page discards the preview</span>}
      </div>
    </div>
  );
};
